import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  Mail,
  Phone,
  FileText,
  Calendar,
  RefreshCw,
  Briefcase,
  CheckSquare,
  Clock,
  ChevronDown,
  ChevronUp,
  Filter,
  MoreHorizontal,
  Loader2,
  Eye,
  MessageSquare,
  DollarSign,
  User,
  Tag,
  ArrowRight,
} from 'lucide-react';

interface ActivityHistoryProps {
  contactId: number;
}

interface Activity {
  id: number;
  type: 'email' | 'call' | 'meeting' | 'note' | 'status_change' | 'deal';
  title: string;
  description: string;
  metadata?: Record<string, any>;
  created_at: string;
  performed_by_name: string;
  performed_by_avatar?: string;
}

type ActivityFilter = 'all' | 'email' | 'call' | 'meeting' | 'note' | 'deal';

interface ActivityConfig {
  icon: React.ReactNode;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const activityConfig: Record<Activity['type'], ActivityConfig> = {
  email: {
    icon: <Mail className="h-4 w-4" />,
    label: 'Email',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
  },
  call: {
    icon: <Phone className="h-4 w-4" />,
    label: 'Call',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/20',
  },
  meeting: {
    icon: <Calendar className="h-4 w-4" />,
    label: 'Meeting',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
  },
  note: {
    icon: <FileText className="h-4 w-4" />,
    label: 'Note',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
  },
  status_change: {
    icon: <RefreshCw className="h-4 w-4" />,
    label: 'Status',
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/20',
  },
  deal: {
    icon: <Briefcase className="h-4 w-4" />,
    label: 'Deal',
    color: 'text-[#2B7FFF]',
    bgColor: 'bg-[#2B7FFF]/10',
    borderColor: 'border-[#2B7FFF]/20',
  },
};

const filters: { value: ActivityFilter; label: string }[] = [
  { value: 'all', label: 'All Activity' },
  { value: 'email', label: 'Emails' },
  { value: 'call', label: 'Calls' },
  { value: 'meeting', label: 'Meetings' },
  { value: 'note', label: 'Notes' },
  { value: 'deal', label: 'Deals' },
];

export function ActivityHistory({ contactId }: ActivityHistoryProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [expandedActivities, setExpandedActivities] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchActivities = async (pageNum = 1) => {
    try {
      const response = await fetch(
        `/api/crm/communications/activity/${contactId}?page=${pageNum}&limit=20`
      );
      if (response.ok) {
        const data = await response.json();
        if (pageNum === 1) {
          setActivities(data.activities);
        } else {
          setActivities((prev) => [...prev, ...data.activities]);
        }
        setHasMore(data.activities.length === 20);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities(1);
  }, [contactId]);

  const filteredActivities = activities.filter(
    (activity) => filter === 'all' || activity.type === filter
  );

  const toggleExpanded = (id: number) => {
    setExpandedActivities((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchActivities(nextPage);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday =
      new Date(now.setDate(now.getDate() - 1)).toDateString() === date.toDateString();

    if (isToday) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    if (isYesterday) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(dateString);
  };

  // Group activities by date
  const groupedActivities = filteredActivities.reduce((groups, activity) => {
    const date = new Date(activity.created_at).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(activity);
    return groups;
  }, {} as Record<string, Activity[]>);

  if (isLoading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B7FFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
        <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        {filters.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors',
              filter === f.value
                ? 'bg-[#2B7FFF] text-white'
                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {filteredActivities.length === 0 ? (
        <div className="text-center py-12">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Clock className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium">No activity yet</h3>
          <p className="text-muted-foreground text-sm mt-1">
            {filter === 'all'
              ? 'Activity will appear here when you interact with this contact'
              : `No ${filter} activity found`}
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-5 top-0 bottom-0 w-px bg-gradient-to-b from-border via-border to-transparent" />

          <div className="space-y-6">
            {Object.entries(groupedActivities).map(([date, dayActivities]) => (
              <div key={date}>
                {/* Date Header */}
                <div className="sticky top-0 z-10 flex items-center gap-3 mb-4 bg-background/95 backdrop-blur-sm py-2">
                  <div className="h-10 w-10 rounded-full bg-muted border-2 border-background flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    {new Date(date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>

                {/* Activities */}
                <div className="space-y-3">
                  {dayActivities.map((activity, index) => {
                    const config = activityConfig[activity.type];
                    const isExpanded = expandedActivities.has(activity.id);

                    return (
                      <motion.div
                        key={activity.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="relative pl-2"
                      >
                        {/* Timeline Dot */}
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: index * 0.05 + 0.1, type: 'spring', stiffness: 500 }}
                          className={cn(
                            'absolute left-4 top-3 h-3 w-3 rounded-full border-2 border-background',
                            config.bgColor.replace('/10', '')
                          )}
                        />

                        {/* Activity Card */}
                        <Card
                          className={cn(
                            'ml-8 transition-all hover:shadow-md',
                            config.borderColor
                          )}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              {/* Icon */}
                              <div
                                className={cn(
                                  'h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0',
                                  config.bgColor
                                )}
                              >
                                <div className={config.color}>{config.icon}</div>
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-medium">{activity.title}</span>
                                      <Badge
                                        variant="secondary"
                                        className={cn('text-[10px] h-5', config.bgColor, config.color)}
                                      >
                                        {config.label}
                                      </Badge>
                                    </div>

                                    {/* Description */}
                                    <p
                                      className={cn(
                                        'text-sm text-muted-foreground mt-1',
                                        !isExpanded && 'line-clamp-2'
                                      )}
                                    >
                                      {activity.description}
                                    </p>

                                    {/* Expand/Collapse */}
                                    {activity.description.length > 100 && (
                                      <button
                                        onClick={() => toggleExpanded(activity.id)}
                                        className="text-xs text-[#2B7FFF] hover:underline mt-1"
                                      >
                                        {isExpanded ? 'Show less' : 'Show more'}
                                      </button>
                                    )}

                                    {/* Metadata display */}
                                    {activity.metadata && activity.type === 'email' && (
                                      <div className="flex items-center gap-2 mt-2">
                                        {activity.metadata.open_count > 0 && (
                                          <Badge
                                            variant="secondary"
                                            className="text-[10px] bg-green-500/10 text-green-600"
                                          >
                                            <Eye className="h-3 w-3 mr-1" />
                                            Opened {activity.metadata.open_count} time
                                            {activity.metadata.open_count > 1 ? 's' : ''}
                                          </Badge>
                                        )}
                                      </div>
                                    )}

                                    {activity.metadata && activity.type === 'call' && (
                                      <div className="flex items-center gap-2 mt-2">
                                        {activity.metadata.duration > 0 && (
                                          <Badge
                                            variant="secondary"
                                            className="text-[10px]"
                                          >
                                            <Clock className="h-3 w-3 mr-1" />
                                            {Math.floor(activity.metadata.duration / 60)}m{' '}
                                            {activity.metadata.duration % 60}s
                                          </Badge>
                                        )}
                                        {activity.metadata.outcome && (
                                          <Badge
                                            variant="secondary"
                                            className="text-[10px] capitalize"
                                          >
                                            {activity.metadata.outcome.replace('_', ' ')}
                                          </Badge>
                                        )}
                                      </div>
                                    )}

                                    {activity.metadata && activity.type === 'status_change' && (
                                      <div className="flex items-center gap-2 mt-2">
                                        <span className="text-xs px-2 py-0.5 rounded bg-muted">
                                          {activity.metadata.from}
                                        </span>
                                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                        <span className="text-xs px-2 py-0.5 rounded bg-[#2B7FFF]/10 text-[#2B7FFF]">
                                          {activity.metadata.to}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Time */}
                                  <span
                                    className="text-xs text-muted-foreground whitespace-nowrap"
                                    title={formatDate(activity.created_at)}
                                  >
                                    {formatRelativeTime(activity.created_at)}
                                  </span>
                                </div>

                                {/* Author */}
                                <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                                  <Avatar className="h-5 w-5">
                                    <AvatarFallback className="text-[10px] bg-[#2B7FFF]/10 text-[#2B7FFF]">
                                      {activity.performed_by_name?.split(' ').map((n) => n[0]).join('') ||
                                        'U'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-xs text-muted-foreground">
                                    {activity.performed_by_name || 'Unknown'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center mt-6">
              <Button variant="outline" onClick={loadMore}>
                Load More
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ActivityHistory;
